import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { AREA_LABELS } from "@/components/BodyMap";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Users, AlertTriangle, Percent, CreditCard, MapPin, Sparkles, Trophy, Share2, History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Traffic-light thresholds — tuned so that a nascent clinic reads as red, a
// healthy program reads as green, and edge cases are yellow.
function bandParticipation(pct) {
  if (pct >= 70) return "green";
  if (pct >= 40) return "yellow";
  return "red";
}
function bandPain(score) {
  // Lower pain is better.
  if (score <= 3) return "green";
  if (score <= 6) return "yellow";
  return "red";
}
function bandEffectiveness(pct) {
  if (pct == null) return "gray";
  if (pct >= 40) return "green";
  if (pct >= 15) return "yellow";
  return "red";
}
const BAND_STYLE = {
  green:  { bg: "#dcfce7", fg: "#166534", ring: "#16a34a" },
  yellow: { bg: "#fef9c3", fg: "#854d0e", ring: "#ca8a04" },
  red:    { bg: "#fee2e2", fg: "#991b1b", ring: "#dc2626" },
  gray:   { bg: "#f4f4f5", fg: "#52525b", ring: "#a1a1aa" },
};
const prettyArea = (a) => AREA_LABELS?.[a] || (a || "").replace(/_/g, " ");

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    (async () => {
      const [a, c, s] = await Promise.all([
        api.get("/clinic/analytics"),
        api.get("/clinics/mine"),
        api.get("/billing/subscription"),
      ]);
      setData(a.data); setClinic(c.data); setSub(s.data);
    })();
  }, []);

  const planActive = sub?.status === "active";
  const renewsOn = sub?.subscription_ends_at?.slice(0, 10);

  const participation = data?.participation_rate ?? 0;
  const avgPain = data?.avg_pain_30d ?? 0;
  const mainArea = data?.main_pain_area;
  const effectiveness = data?.stretch_effectiveness; // may be null when insufficient signal

  const strip = [
    {
      key: "participation",
      icon: Users,
      label: "Participation rate",
      value: `${participation}%`,
      caption: participation >= 70 ? "Great engagement" : participation >= 40 ? "Room to grow" : "Needs attention",
      band: bandParticipation(participation),
      tid: "strip-participation",
    },
    {
      key: "pain",
      icon: AlertTriangle,
      label: "Avg pain (30d)",
      value: avgPain,
      caption: avgPain <= 3 ? "Healthy range" : avgPain <= 6 ? "Watch closely" : "Elevated",
      band: bandPain(avgPain),
      tid: "strip-pain",
    },
    {
      key: "main-area",
      icon: MapPin,
      label: "Main pain area",
      value: mainArea ? prettyArea(mainArea) : "—",
      caption: mainArea ? "Most-reported region" : "No data yet",
      band: "gray",
      tid: "strip-main-area",
    },
    {
      key: "effectiveness",
      icon: Sparkles,
      label: "Stretch effectiveness",
      value: effectiveness == null ? "—" : `${effectiveness}%`,
      caption: effectiveness == null
        ? "Log more stretches to unlock"
        : effectiveness >= 40 ? "Strong impact"
        : effectiveness >= 15 ? "Some benefit"
        : "Little impact so far",
      band: bandEffectiveness(effectiveness),
      tid: "strip-effectiveness",
    },
  ];

  return (
    <div>
      <span className="pill">Clinic Manager</span>
      <h1 className="mt-2 text-3xl font-bold">{clinic?.name || "Your clinic"}</h1>
      <p className="text-zinc-500 mt-1 text-sm">All metrics below are aggregated and fully anonymized. Welcome, {user?.name}.</p>

      {/* "This month's win" ribbon — shown only when we have a strong signal */}
      {data?.monthly_win && (
        <MonthlyWinRibbon win={data.monthly_win} clinicName={clinic?.name} />
      )}

      {/* Pain-analytics privacy gate — shown when the clinic hasn't yet met the
          minimum data + k-anonymity thresholds for revealing pain aggregates */}
      {data?.privacy_gate && !data.privacy_gate.allowed && (
        <div
          className="mt-5 rounded-xl border px-4 py-3 flex flex-wrap items-start gap-3"
          style={{ background: "#f0f9ff", borderColor: "#7dd3fc" }}
          data-testid="pain-privacy-gate"
        >
          <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#e0f2fe" }}>
            <AlertTriangle className="w-5 h-5" style={{ color: "#0369a1" }} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-sky-700">
              Pain analytics locked
            </div>
            <div className="mt-0.5 text-sm text-sky-900" data-testid="pain-privacy-gate-reason">
              {data.privacy_gate.reason === "no_entries" && "No sonographer has logged pain yet."}
              {data.privacy_gate.reason === "time" && `Waiting on ${data.privacy_gate.unlocks_in_days} more day${data.privacy_gate.unlocks_in_days === 1 ? "" : "s"} of check-ins (need one full ${data.privacy_gate.cadence} cycle).`}
              {data.privacy_gate.reason === "k_anon" && `Only ${data.privacy_gate.sonographers_logged} sonographer${data.privacy_gate.sonographers_logged === 1 ? " has" : "s have"} logged pain — need at least ${data.privacy_gate.required_sonographers} to keep individual entries anonymous.`}
              {data.privacy_gate.reason === "both" && `Waiting on ${data.privacy_gate.unlocks_in_days} more day${data.privacy_gate.unlocks_in_days === 1 ? "" : "s"} and ${Math.max(0, data.privacy_gate.required_sonographers - data.privacy_gate.sonographers_logged)} more sonographer${(data.privacy_gate.required_sonographers - data.privacy_gate.sonographers_logged) === 1 ? "" : "s"} to log pain entries.`}
            </div>
            <div className="text-[11px] text-sky-700/80 mt-1">
              Payroll timesheets and staff counts are unaffected. Pain aggregates unlock once both privacy thresholds are met.
            </div>
          </div>
        </div>
      )}

      {/* Top KPI strip — traffic-light coloured */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="clinic-kpi-strip">
        {strip.map((k) => {
          const s = BAND_STYLE[k.band];
          const Icon = k.icon;
          return (
            <div
              key={k.key}
              className="rounded-xl border p-5"
              style={{ background: s.bg, borderColor: `${s.ring}55` }}
              data-testid={k.tid}
            >
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold" style={{ color: s.fg }}>
                <Icon className="w-4 h-4" /> {k.label}
              </div>
              <div className="mt-3 text-3xl font-black" style={{ color: s.fg }} data-testid={`${k.tid}-value`}>
                {k.value}
              </div>
              <div className="mt-1 text-xs font-semibold" style={{ color: s.fg, opacity: 0.85 }} data-testid={`${k.tid}-caption`}>
                {k.caption}
              </div>
            </div>
          );
        })}
      </div>

      <div className="card-elev mt-6 flex flex-wrap items-center gap-4 justify-between" data-testid="subscription-card">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: "var(--gold-light)" }}>
            <CreditCard className="w-5 h-5" style={{ color: "var(--gold-hover)" }} />
          </span>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500">Subscription</div>
            <div className="font-bold capitalize" data-testid="subscription-plan">
              {planActive ? `${sub.plan} plan — active` : "Free trial · no plan selected"}
            </div>
            {planActive && renewsOn && (
              <div className="text-xs text-zinc-500 mt-0.5" data-testid="subscription-renews">Renews on {renewsOn}</div>
            )}
          </div>
        </div>
        {!planActive ? (
          <Link to="/pricing" className="btn-gold h-10 px-5 text-sm" data-testid="subscription-upgrade">Choose a plan</Link>
        ) : (
          <Link to="/pricing" className="btn-outline h-10 px-5 text-sm" data-testid="subscription-manage">View plans</Link>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="card-elev">
          <h3 className="font-bold mb-4">Clinic pain trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data?.trend || []}>
              <CartesianGrid stroke="#f4f4f5" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
              <YAxis domain={[0, 10]} stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", borderRadius: 8 }} />
              <Line type="monotone" dataKey="avg_pain" stroke="#09090b" strokeWidth={3} dot={{ r: 3, fill: "#09090b" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="card-elev">
          <h3 className="font-bold mb-4">Top hotspots</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data?.hotspots || []}>
              <CartesianGrid stroke="#f4f4f5" />
              <XAxis dataKey="area" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", borderRadius: 8 }} />
              <Bar dataKey="reports" fill="#d4af37" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function MonthlyWinRibbon({ win, clinicName }) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const prettyArea = (a) => AREA_LABELS?.[a] || (a || "").replace(/_/g, " ");
  const humanArea = prettyArea(win.area);
  const shareText = `📉 This month at ${clinicName || "our clinic"}: ${humanArea} stretching cut ${humanArea} pain by ${win.reduction_pct}% (Sonote).`;
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "Sonote monthly win", text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
      }
    } catch { /* user dismissed / no clipboard */ }
  };
  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      const { data } = await api.get("/clinic/wins");
      setHistory(data || []);
    } catch { setHistory([]); }
  };
  return (
    <div
      className="mt-5 rounded-2xl px-5 py-4 flex flex-wrap items-center gap-4 border"
      style={{ background: "linear-gradient(90deg, var(--gold-light) 0%, #fff 65%)", borderColor: "var(--gold)" }}
      data-testid="monthly-win-ribbon"
    >
      <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--gold)" }}>
        <Trophy className="w-5 h-5 text-white" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold" style={{ color: "var(--gold-hover)" }}>
          This month&apos;s win
        </div>
        <div className="mt-0.5 text-lg font-bold text-zinc-900" data-testid="monthly-win-headline">
          <span className="capitalize">{humanArea}</span> stretching cut <span className="capitalize">{humanArea}</span> pain{" "}
          <span style={{ color: "var(--gold-hover)" }}>–{win.reduction_pct}%</span>
        </div>
        <div className="text-xs text-zinc-500 mt-0.5">
          {win.confidence === "strong" ? "Strong" : "Moderate"} correlation (r = {win.correlation}) across {win.paired_days} days · anonymized
        </div>
      </div>
      <div className="flex items-center gap-2 ml-auto">
        <button onClick={openHistory} className="btn-ghost text-xs h-9 px-3" data-testid="monthly-win-history">
          <History className="w-4 h-4 mr-1" />History
        </button>
        <button onClick={share} className="btn-outline text-xs h-9 px-4" data-testid="monthly-win-share">
          <Share2 className="w-4 h-4 mr-1" />Share
        </button>
      </div>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg" data-testid="win-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-4 h-4" style={{ color: "var(--gold-hover)" }} />
              Monthly wins timeline
            </DialogTitle>
          </DialogHeader>
          {history.length === 0 ? (
            <p className="text-sm text-zinc-500 mt-3">No prior months on record yet. Every month with a strong-enough signal will land here automatically.</p>
          ) : (
            <ol className="mt-3 space-y-3">
              {[...history].reverse().map((h) => (
                <li key={h.year_month} className="flex items-start gap-3" data-testid={`win-history-${h.year_month}`}>
                  <span className="mt-1 w-2 h-2 rounded-full shrink-0" style={{ background: h.confidence === "strong" ? "var(--gold)" : "#a1a1aa" }} />
                  <div className="min-w-0">
                    <div className="text-xs text-zinc-500">{h.year_month}</div>
                    <div className="font-semibold">
                      <span className="capitalize">{prettyArea(h.area)}</span> stretching cut pain{" "}
                      <span style={{ color: "var(--gold-hover)" }}>–{h.reduction_pct}%</span>
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      {h.confidence === "strong" ? "Strong" : "Moderate"} · r = {h.correlation} · {h.paired_days} days
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
          <p className="mt-4 text-[11px] text-zinc-400 border-t border-zinc-100 pt-3">
            Sonote captures a rolling snapshot every month a clinic hits a moderate-or-stronger stretching signal — building a compounding record you can point to at reviews.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
